import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserAppealsQuery, useAppealTopicsQuery } from '../../hooks/useUserAppealsQuery';
import useCleanPaginationURL from '../../hooks/useCleanPaginationURL';
import { usePaginationTextReplace } from '../../hooks/usePaginationTextReplace';
import { useUserContext } from '../../context/user';
import { isAllowAction } from '../../services/permissions/utils';
import { UserAppealResponse, AppealTopic, getAppealFromString, getColorVariantForAppealTopic, AppealTopicResponse } from '../../types/api/appeals';
import { FeedbackStatus, getFeedbackStatusColor, getFeedbackStatusLabel, statusFilterConfig } from '../../types/api/feedback';
import Segment from '../navigation/Segment/Segment';
import { usersTabsData } from '../navigation/Segment/Segment+Data';
import { Placeholder, createErrorPlaceholder } from '../basic';
import { CreateUserAppealAnswerModal } from './components/CreateUserAppealAnswerModal';
import RegularPageLayout from '../../layouts/RegularPageLayout';
import { UserTableRow } from '../basic/UserTableRow';
import { userAppealsListTableConfig } from './UserAppealsList+Table';
import { renderTruncatedText } from '../../helpers/strings';
import { pageSize } from '../../services/settings';
import { Button, Stack, Label, ActionMenu, ActionList, IconButton } from '@primer/react'
import { Table, DataTable } from '@primer/react/experimental'
import { XCircleIcon, FilterIcon, BriefcaseIcon } from '@primer/octicons-react';
import { formatDate, formatDateTime } from '../../helpers/dates';

export default function UserAppealsList() {
    const { profile } = useUserContext();

    const navigate = useNavigate();

    const [pageIndex, setPageIndex] = React.useState(1)
    const [selectedFilter, setSelectedFilter] = useState<AppealTopicResponse | undefined>(undefined);
    const [selectedStatus, setSelectedStatus] = useState<FeedbackStatus | undefined>(undefined);
    const [topics, setTopics] = useState<AppealTopicResponse[]>([]);
    const [selectedAppeals, setSelectedAppeals] = useState<UserAppealResponse | undefined>(undefined);
    const [openAppealsModal, setOpenAppealsModal] = React.useState(false)

    const {
        isLoading,
        data,
        refetch,
        isFetched,
        isError
    } = useUserAppealsQuery(
        "",
        pageIndex,
        pageSize,
        "-created_at",
        selectedFilter?.id,
        selectedStatus
    );

    const {
        isLoading: loadingTopics,
        data: dataTopics,
        refetch: refetchTopics
    } = useAppealTopicsQuery(1, 100);

    useCleanPaginationURL();
    usePaginationTextReplace()

    useEffect(() => {
        refetch()
    }, [pageIndex]);

    useEffect(() => {
        if (dataTopics) {
            setTopics(dataTopics.list)
        }
    }, [dataTopics]);

    const emptyListDescription = `В сервисе ещё нет обращений
от пользователей`;

    const openDisplayAppealModal = (appeal: UserAppealResponse) => {
        setSelectedAppeals(appeal)
        setOpenAppealsModal(true)
    }

    const appealDismissed = () => {
        setSelectedAppeals(undefined)
        setOpenAppealsModal(false)
    }

    const appealAnswerSended = () => {
        setSelectedAppeals(undefined)
        setOpenAppealsModal(false)
        refetch()
    }

    const appealAnswerChanged = () => {
        refetch()
    }

    const userFileDeleted = () => {
        refetch()
    }

    return (
        <RegularPageLayout
            header={
                <Stack justify="space-between" direction="horizontal">
                    <Segment items={usersTabsData(profile)} />
                    <Stack direction="horizontal">
                        <ActionMenu>
                            <ActionMenu.Anchor>
                                <IconButton icon={FilterIcon} aria-label="Фильтр" />
                            </ActionMenu.Anchor>
                            <ActionMenu.Overlay>
                                <ActionList
                                    selectionVariant="single"
                                    role="menu"
                                    aria-label="Item"
                                    className='w-[232px]'
                                >
                                    <ActionList.Item
                                        key="all"
                                        role="menuitemradio"
                                        selected={selectedFilter === undefined && selectedStatus === undefined}
                                        aria-checked={selectedFilter === undefined && selectedStatus === undefined}
                                        onSelect={() => {
                                            setSelectedFilter(undefined)
                                            setSelectedStatus(undefined)
                                        }}
                                    >
                                        <Stack align="center" direction="horizontal" gap='condensed'>
                                            <BriefcaseIcon size={16} />
                                            Все обращения
                                        </Stack>
                                    </ActionList.Item>
                                    <ActionList.Divider className='block mt-2' />
                                    {Object.entries(statusFilterConfig).map(([status, config]) => {
                                        const Icon = config.icon;
                                        return (
                                            <ActionList.Item
                                                key={status}
                                                role="menuitemradio"
                                                selected={selectedStatus === status}
                                                aria-checked={selectedStatus === status}
                                                onSelect={() => setSelectedStatus(status as FeedbackStatus)}
                                            >
                                                <Stack align="center" direction="horizontal" gap='condensed'>
                                                    <Icon size={16} />
                                                    {config.label}
                                                </Stack>
                                            </ActionList.Item>
                                        );
                                    })}
                                    <ActionList.Divider className='block mt-2' />
                                    {topics.map((item, index) => (
                                        <ActionList.Item
                                            key={index}
                                            role="menuitemradio"
                                            selected={item === selectedFilter}
                                            aria-checked={item === selectedFilter}
                                            onSelect={() => setSelectedFilter(item)}
                                        >
                                            <Stack align="center" direction="horizontal" gap='none'>
                                                {item.title}
                                            </Stack>
                                        </ActionList.Item>
                                    ))}
                                </ActionList>
                            </ActionMenu.Overlay>
                        </ActionMenu>
                    </Stack>
                </Stack>
            }
            content={
                <Stack>
                    {selectedAppeals && <CreateUserAppealAnswerModal
                        isOpen={openAppealsModal}
                        appeal={selectedAppeals}
                        onDidAnswerSended={appealAnswerSended}
                        onDidAnswerStatusChanged={appealAnswerChanged}
                        onDidUserFileDeleted={userFileDeleted}
                        onClose={appealDismissed}
                    />}

                    {isError && !isLoading && createErrorPlaceholder()}
                    {isLoading && <Table.Container>
                        <Table.Skeleton
                            aria-labelledby="users-loading"
                            rows={pageSize}
                            columns={userAppealsListTableConfig().map((el) => {
                                return {
                                    header: el.header,
                                    id: el.field,
                                }
                            })}
                        />
                    </Table.Container>}
                    {isFetched && data?.list.length === 0 && (
                        <Placeholder
                            icon={<XCircleIcon size={24} className='opacity-40' />}
                            header='Обращения не найдены'
                            description={emptyListDescription}
                            button={undefined}
                        />
                    )}
                    {isFetched && data && data.list.length > 0 &&
                        <Table.Container>
                            <DataTable
                                aria-labelledby="users-table"
                                data={data.list}
                                columns={[
                                    {
                                        id: 'name',
                                        rowHeader: true,
                                        header: "Пользователь",
                                        renderCell: (row) => {
                                            const user = (row as UserAppealResponse).user;
                                            return <UserTableRow
                                                user={user}
                                                onClick={() => user && navigate('/users/user?id=' + user.id)} />
                                        }
                                    },
                                    {
                                        id: 'phone',
                                        header: "Номер телефона",
                                        renderCell: (row) => {
                                            const appeal = row as UserAppealResponse;
                                            if (!appeal.user) {
                                                return '-';
                                            }
                                            if (appeal.user.username === 'Unknown User' && !appeal.user.phone) {
                                                return '-';
                                            }
                                            return appeal.user.phone;

                                        }
                                    },
                                    {
                                        id: 'email',
                                        header: "E-mail",
                                        renderCell: (row) => {
                                            const appeal = row as UserAppealResponse;
                                            return appeal.email ?? appeal.user?.email
                                        }
                                    },
                                    {
                                        id: 'type',
                                        header: "Тип обращения",
                                        renderCell: (row) => {
                                            const topicModel = (row as UserAppealResponse).topic;
                                            let appealTopic = getAppealFromString(topicModel.title)
                                            let variant = getColorVariantForAppealTopic(appealTopic ?? AppealTopic.Other)
                                            return <Label variant={variant}>{topicModel.title}</Label>
                                        }
                                    },
                                    {
                                        id: 'text',
                                        header: 'Обращение',
                                        renderCell: (row) => {
                                            const text = (row as UserAppealResponse).text;
                                            return renderTruncatedText(text, 32)
                                        }
                                    },
                                    {
                                        id: 'date',
                                        header: 'Дата и время',
                                        renderCell: (row) => {
                                            const date = (row as UserAppealResponse).createdAt;
                                            return formatDateTime(date, ',')
                                        }
                                    },
                                    {
                                        id: 'status',
                                        header: 'Статус',
                                        renderCell: (row) => {
                                            const status = (row as UserAppealResponse).status;
                                            const color = getFeedbackStatusColor(status)
                                            return <p style={{ color: color }}>{getFeedbackStatusLabel(status)}</p>
                                        }
                                    },
                                    {
                                        id: 'actions',
                                        header: '',
                                        renderCell: (row) => {
                                            const appeal = (row as UserAppealResponse);
                                            const isAllowReply = isAllowAction(profile, 'REPLY_APPEALS')
                                            const isAllowView = isAllowAction(profile, 'VIEW_USER_APPEALS')

                                            return isAllowView && (
                                                <Button
                                                    onClick={() => openDisplayAppealModal(appeal)}
                                                    size="small"
                                                    aria-label="Комментарий">{isAllowReply ? 'Ответить' : 'Просмотр'}
                                                </Button>
                                            )
                                        }
                                    },
                                ]}
                            />
                        </Table.Container>
                    }
                    {isFetched && data && data.list.length > 0 &&
                        (<div className="sticky bottom-0 bg-white border-t border-gray-200 z-10 -mt-4">
                            <Table.Pagination aria-label="pagination" key={pageIndex} pageSize={pageSize} totalCount={data.totalCount} onChange={({
                                pageIndex
                            }) => {
                                setPageIndex(pageIndex + 1);
                            }} defaultPageIndex={pageIndex - 1} />
                        </div>
                        )}
                </Stack>
            }
        ></RegularPageLayout>

    )
}
